import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import type { ChannelMessage } from "@/lib/chat-room/messages";
import { ChatMessageItem } from "./ChatMessageItem";

interface ChatMessageListProps {
  messages: ChannelMessage[];
  selfId: string | null;
  /** Indeks for «N nye»-streken, -1 for ingen. */
  dividerIndex: number;
  dividerLabel: string;
  isLoading: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  userName: (userId: string) => string | null;
  renderFooter?: (message: ChannelMessage) => ReactNode;
  renderLink?: (message: ChannelMessage) => ReactNode;
}

/** Meldingslisten i Chat-rommet. Ruller til bunnen når kanalen åpnes og når nye meldinger kommer. */
export function ChatMessageList({
  messages,
  selfId,
  dividerIndex,
  dividerLabel,
  isLoading,
  hasMore,
  isFetchingMore,
  onLoadMore,
  userName,
  renderFooter,
  renderLink,
}: ChatMessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastId = messages[messages.length - 1]?.id;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastId]);

  const mentionedByMessage = useMemo(() => {
    const map = new Map<string, { id: string; full_name: string }[]>();
    for (const m of messages) {
      map.set(
        m.id,
        m.mentionedUserIds
          .map((id) => ({ id, full_name: userName(id) ?? "" }))
          .filter((u) => u.full_name),
      );
    }
    return map;
  }, [messages, userName]);

  return (
    <div ref={scrollRef} role="log" aria-label="Meldinger" className="flex flex-1 flex-col overflow-y-auto px-5 pb-3 pt-2">
      {hasMore && (
        <div className="flex justify-center py-2">
          <Button size="sm" variant="outline" onClick={onLoadMore} disabled={isFetchingMore} className="text-xs">
            {isFetchingMore && <SrIcon name="loader2" className="mr-1.5 h-3 w-3 animate-spin" />}
            Last eldre meldinger
          </Button>
        </div>
      )}
      {isLoading && (
        <div className="flex justify-center py-8">
          <SrIcon name="loader2" className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
      {!isLoading && messages.length === 0 && (
        <p className="m-auto py-12 text-center text-sm text-muted-foreground">Ingen meldinger ennå. Skriv den første!</p>
      )}
      {messages.map((m, i) => (
        <div key={m.id}>
          {i === dividerIndex && (
            <div
              role="separator"
              className="mb-1 mt-3.5 flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-destructive"
            >
              <span className="h-[1.5px] flex-1 bg-destructive" />
              {dividerLabel}
              <span className="h-[1.5px] flex-1 bg-destructive" />
            </div>
          )}
          <ChatMessageItem
            message={m}
            selfId={selfId}
            mentionedUsers={mentionedByMessage.get(m.id) ?? []}
            footer={renderFooter?.(m)}
            link={renderLink?.(m)}
          />
        </div>
      ))}
    </div>
  );
}
